import {
  type ChangeOrderPricingMode,
  type ChangeOrderPricingType,
  autoClientPriceInclFromBuilderCost,
  computeMarginExcl,
  formatCurrencyAbs,
  fromInclGst,
  isMoneyInputPartial,
  parseMoneyInput,
  toPersistedSignedAmount,
} from '@/lib/changeOrderPricing';

interface ChangeOrderPricingFieldsProps {
  pricingType: ChangeOrderPricingType;
  onPricingTypeChange: (type: ChangeOrderPricingType) => void;
  pricingMode: ChangeOrderPricingMode;
  onPricingModeChange: (mode: ChangeOrderPricingMode) => void;
  /** Absolute (non-negative) form strings. */
  builderCost: string;
  onBuilderCostChange: (value: string) => void;
  clientPrice: string;
  onClientPriceChange: (value: string) => void;
  /** When true, other commercial fields may be locked — Client Price stays editable. */
  showLockedHint?: boolean;
  clientPriceError?: string | null;
  builderCostError?: string | null;
  builderCostDisabled?: boolean;
  idPrefix?: string;
}

export default function ChangeOrderPricingFields({
  pricingType,
  onPricingTypeChange,
  pricingMode,
  onPricingModeChange,
  builderCost,
  onBuilderCostChange,
  clientPrice,
  onClientPriceChange,
  showLockedHint = false,
  clientPriceError = null,
  builderCostError = null,
  builderCostDisabled = false,
  idPrefix = 'co',
}: ChangeOrderPricingFieldsProps) {
  const isCredit = pricingType === 'credit';
  const builderNum = parseMoneyInput(builderCost);
  const clientNum = parseMoneyInput(clientPrice);
  const clientBreakdown =
    clientNum != null && clientNum >= 0 ? fromInclGst(clientNum) : null;

  // Preview signed DB values for margin (excl. GST basis)
  const signedBuilder =
    builderNum != null ? toPersistedSignedAmount(String(builderNum), pricingType) : null;
  const signedClient =
    clientNum != null ? toPersistedSignedAmount(String(clientNum), pricingType) : null;
  const margin = computeMarginExcl(signedBuilder, signedClient);

  const builderLabel = isCredit
    ? 'Builder Cost Reduction (excl. GST)'
    : 'Builder Cost (excl. GST)';
  const clientLabel = isCredit
    ? 'Client Credit (incl. GST)'
    : 'Client Price (incl. GST)';

  function handleBuilderCostChange(raw: string) {
    // Form always accepts absolute non-negative typing for both types.
    if (raw !== '' && !isMoneyInputPartial(raw, { allowNegative: false })) return;
    onBuilderCostChange(raw);

    if (pricingMode !== 'auto') return;
    const cost = parseMoneyInput(raw);
    if (cost == null) {
      if (raw.trim() === '') onClientPriceChange('');
      return;
    }
    if (cost < 0) return;
    onClientPriceChange(String(autoClientPriceInclFromBuilderCost(cost)));
  }

  function handleClientPriceChange(raw: string) {
    if (raw !== '' && !isMoneyInputPartial(raw, { allowNegative: false })) return;
    onClientPriceChange(raw);
  }

  function handlePricingTypeChange(type: ChangeOrderPricingType) {
    onPricingTypeChange(type);
    // Keep absolute field values; only persistence sign changes.
  }

  function handleModeChange(mode: ChangeOrderPricingMode) {
    onPricingModeChange(mode);
    if (mode === 'auto') {
      const cost = parseMoneyInput(builderCost);
      if (cost != null && cost >= 0) {
        onClientPriceChange(String(autoClientPriceInclFromBuilderCost(cost)));
      }
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="block text-[#555555] text-xs font-semibold mb-1.5">Change Order Type</p>
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              {
                value: 'addition' as const,
                label: 'Addition',
                hint: 'Extra work — positive charge',
              },
              {
                value: 'credit' as const,
                label: 'Credit / Omission',
                hint: 'Reduction — saved as negative values',
              },
            ] as const
          ).map((opt) => {
            const active = pricingType === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handlePricingTypeChange(opt.value)}
                className={`text-left rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
                  active
                    ? opt.value === 'credit'
                      ? 'border-[#7C3AED] bg-[#F5F3FF]'
                      : 'border-[#E8621A] bg-[#FEF0E7]'
                    : 'border-[#E0DDD8] bg-white hover:border-[#E8621A]/50'
                }`}
              >
                <p
                  className={`text-xs font-semibold ${
                    active
                      ? opt.value === 'credit'
                        ? 'text-[#7C3AED]'
                        : 'text-[#E8621A]'
                      : 'text-[#374151]'
                  }`}
                >
                  {opt.label}
                </p>
                <p className="text-[10px] text-[#9CA3AF] mt-0.5">{opt.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="block text-[#555555] text-xs font-semibold mb-1.5">Pricing method</p>
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              {
                value: 'auto' as const,
                label: 'Auto calculate',
                hint: isCredit
                  ? 'From cost reduction + 15% GST'
                  : 'From builder cost + 15% GST',
              },
              {
                value: 'manual' as const,
                label: isCredit ? 'Manual client credit' : 'Manual client price',
                hint: isCredit
                  ? 'Agreed GST-inclusive credit'
                  : 'Agreed GST-inclusive total',
              },
            ] as const
          ).map((opt) => {
            const active = pricingMode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleModeChange(opt.value)}
                className={`text-left rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
                  active
                    ? 'border-[#E8621A] bg-[#FEF0E7]'
                    : 'border-[#E0DDD8] bg-white hover:border-[#E8621A]/50'
                }`}
              >
                <p className={`text-xs font-semibold ${active ? 'text-[#E8621A]' : 'text-[#374151]'}`}>
                  {opt.label}
                </p>
                <p className="text-[10px] text-[#9CA3AF] mt-0.5">{opt.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label
            htmlFor={`${idPrefix}-builder-cost`}
            className="block text-[#555555] text-xs font-semibold mb-1.5"
          >
            {builderLabel}
            <span className="ml-1 text-[10px] font-normal text-[#EF4444]">(internal only)</span>
          </label>
          <input
            id={`${idPrefix}-builder-cost`}
            type="text"
            inputMode="decimal"
            value={builderCost}
            onChange={(e) => handleBuilderCostChange(e.target.value)}
            placeholder="0.00"
            disabled={builderCostDisabled}
            className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#E8621A] ${
              builderCostDisabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''
            } ${builderCostError ? 'border-[#FECACA]' : ''}`}
          />
          {isCredit && (
            <p className="text-[10px] text-[#7C3AED] mt-1">
              Enter a positive amount. Saved as a negative reduction in the database.
            </p>
          )}
          {builderCostError && (
            <p className="text-[10px] text-[#EF4444] mt-1">{builderCostError}</p>
          )}
        </div>
        <div>
          <label
            htmlFor={`${idPrefix}-client-price`}
            className="block text-[#555555] text-xs font-semibold mb-1.5"
          >
            {clientLabel}
            <span className="ml-1 text-[10px] font-normal text-[#059669]">(client-visible)</span>
          </label>
          <input
            id={`${idPrefix}-client-price`}
            type="text"
            inputMode="decimal"
            value={clientPrice}
            onChange={(e) => handleClientPriceChange(e.target.value)}
            placeholder="0.00"
            readOnly={pricingMode === 'auto'}
            className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#E8621A] ${
              pricingMode === 'auto' ? 'bg-[#F9FAFB] text-[#374151]' : ''
            } ${clientPriceError ? 'border-[#FECACA]' : ''}`}
          />
          {pricingMode === 'auto' && (
            <p className="text-[10px] text-[#9CA3AF] mt-1">
              Auto: absolute builder amount (excl. GST) × 1.15. Switch to Manual for an agreed figure.
            </p>
          )}
          {isCredit && pricingMode === 'manual' && (
            <p className="text-[10px] text-[#7C3AED] mt-1">
              Enter a positive credit. Saved as a negative client_price (incl. GST).
            </p>
          )}
          {pricingMode === 'manual' && showLockedHint && (
            <p className="text-[10px] text-[#059669] mt-1">
              Pricing remains editable for management on approved or closed change orders.
            </p>
          )}
          {clientPriceError && (
            <p className="text-[10px] text-[#EF4444] mt-1">{clientPriceError}</p>
          )}
        </div>
      </div>

      {clientBreakdown && (
        <div
          className={`rounded-xl border px-3 py-2.5 grid grid-cols-3 gap-2 ${
            isCredit
              ? 'border-[#DDD6FE] bg-[#F5F3FF]'
              : 'border-[#BBF7D0] bg-[#F0FDF4]'
          }`}
        >
          <div>
            <p className="text-[10px] text-[#6B7280]">Excl. GST</p>
            <p className="text-xs font-semibold text-[#374151]">
              ${formatCurrencyAbs(clientBreakdown.excl)}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-[#6B7280]">GST (15%)</p>
            <p className="text-xs font-semibold text-[#374151]">
              ${formatCurrencyAbs(clientBreakdown.gst)}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-[#6B7280]">{isCredit ? 'Credit incl. GST' : 'Incl. GST'}</p>
            <p className={`text-xs font-bold ${isCredit ? 'text-[#7C3AED]' : 'text-[#059669]'}`}>
              ${formatCurrencyAbs(clientBreakdown.incl)}
            </p>
          </div>
        </div>
      )}

      {margin != null && (
        <div className="rounded-xl border border-gray-100 bg-[#F9FAFB] px-3 py-2.5 space-y-1">
          <p className="text-[10px] text-[#6B7280]">
            {isCredit ? 'Retained margin' : 'Margin'} (excl. GST):
            {' '}client_price ÷ 1.15 − builder_cost
          </p>
          <p
            className={`text-sm font-bold ${
              margin >= 0 ? 'text-[#059669]' : 'text-[#EF4444]'
            }`}
          >
            ${formatCurrencyAbs(margin)}
            {isCredit ? ' retained' : ''}
          </p>
          {signedBuilder != null && signedClient != null && (
            <p className="text-[10px] text-[#9CA3AF]">
              Will save builder_cost={signedBuilder}, client_price={signedClient}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
